import React, { useState, useEffect, useMemo } from 'react'

import {
    Box, Grid, FormControl, InputLabel, Select, MenuItem, Tooltip, IconButton,
    Paper, Divider, InputBase, List, Button, ListItemText, Dialog, DialogTitle, DialogContent,
    TextField, InputAdornment
} from '@mui/material'

import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    gridClasses,
    useGridApiRef,
    LicenseInfo,
    ptBR
} from '@mui/x-data-grid-pro';

import { ptBR as corePtBR } from '@mui/material/locale';

import {
    createTheme,
    ThemeProvider,
    StyledEngineProvider
} from '@mui/material/styles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { utils, writeFile } from 'xlsx';
import EmojiTextarea from '../../Input/EmojiTextarea';
import useNotification from '../../Notification';
import api from '../../../api';
import LoadingButton from '@mui/lab/LoadingButton'
import PredefinedMessagesModal from './PredefinedMessagesModal';
import ImportMessages from './ImportMessages';
import ConfirmationDialog from '../../Dialogs/Confirmation';
import Loading from '../../Loading';
import { emojiReplaceJSX } from '../../../utils/emojiDict';
import { stringOperators } from '../../../utils/filterOperators';
import ReactHtmlParser from 'react-html-parser';

import { getSession } from "../../../auth"

const theme = createTheme({
    palette: {
        primary: { main: '#21446C' },
    },
}, ptBR, corePtBR);




const PredefinedMessages = ({ enterpriseId }) => {

    const [loading, setLoading] = useState(false)
    const [messages, setMessages] = useState([])
    const [allMessages, setAllMessages] = useState([])
    const [selectedSources, setSelectedSources] = useState(['enterprise'])
    const [operators, setOperators] = useState([])
    const [loadedOperators, setLoadedOperators] = useState(new Set())
    const [userNames, setUserNames] = useState({})
    const [selected, setSelected] = useState()
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showPredefinedMessages, setShowPredefinedMessages] = useState(false)
    const [htmlPreview, setHtmlPreview] = useState(null)
    const [search, setSearch] = useState("")
    const [showImportMessages, setShowImportMessages] = useState(false)
    const apiRef = useGridApiRef()

    const exportMessages = () => {
        const queues = getSession().profile?.enterprise?.queues || []
        const rows = Array.from(apiRef.current.getVisibleRowModels().values()).map(message => {
            const queueIds = message.queue_ids || message.queues || [message.queue_id]
            const ids = [...new Set(queueIds.map(value => Number(typeof value === 'object' ? value.id ?? value.queue_id ?? value.queue : value)).filter(value => value > 0))]
            return [
                String(message.id),
                message.emojized_message || message.message,
                message.category,
                ids.length ? ids.map(id => queues.find(queue => queue.id == id)?.description || `Fila não encontrada (${id})`).join('; ') : 'Qualquer Fila',
                message.source === 'enterprise' ? 'Geral' : `Operador: ${message.user_name}`
            ]
        })
        const worksheet = utils.aoa_to_sheet([['Identificação', 'Mensagem', 'Categoria', 'Fila', 'Tipo'], ...rows])
        worksheet['!cols'] = [{ wch: 16 }, { wch: 80 }, { wch: 25 }, { wch: 35 }, { wch: 30 }]
        const workbook = utils.book_new()
        utils.book_append_sheet(workbook, worksheet, 'Mensagens predefinidas')
        const extension = rows.some(row => row.some(value => String(value).length > 32767)) ? 'csv' : 'xlsx'
        writeFile(workbook, `mensagens_predefinidas.${extension}`, { compression: true })
    }

    const CustomToolbar = () => {

        return (
            <GridToolbarContainer className={gridClasses.toolbarContainer}>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <LoadingButton
                    size="small"
                    component="label"
                    startIcon={<AddCircleIcon />}
                    onClick={(e) => { setShowPredefinedMessages(true); setSelected() }}
                >
                    Adicionar
                </LoadingButton>
                <Button size="small" startIcon={<DownloadIcon />} onClick={exportMessages} disabled={!filteredMessages.length}>
                    Exportar
                </Button>
                <Button size="small" startIcon={<UploadIcon />} onClick={() => setShowImportMessages(true)}>
                    Importar
                </Button>
            </GridToolbarContainer>
        );
    }

    const deleteMessage = async () => {
        if (!selected) return;

        setLoading(true)
        const url = selected.source === 'enterprise'
            ? `/api/enterprises/${enterpriseId}/predefinedmessages/${selected.id}/`
            : `/api/users/${selected.user_id}/predefinedmessages/${selected.id}/`
        const res = await api.delete(url)
        setLoading(false)

        if (res.status == 204) {
            useNotification(
                'Sucesso!',
                `Mensagem predefinida deletada com sucesso.`,
                'success'
            );
            const updatedAllMessages = allMessages.filter(m => m.uniqueId !== selected.uniqueId)
            setAllMessages(updatedAllMessages)
            filterMessages(updatedAllMessages, selectedSources)
            setShowDeleteDialog(false);
        }
        else {
            useNotification(
                'Erro!',
                'Ocorreu um erro interno ao deletar a mensagem predefinida.',
                'danger'
            );
        }
    }

    const addMessage = async (text, category, queue = 0, id = 0, isHtml = false, htmlTitle = null) => {
        const queueSelection = Array.isArray(queue) ? queue : [queue]
        const normalizedQueueIds = [...new Set(
            queueSelection
                .map((value) => Number(value))
                .filter((value) => !Number.isNaN(value) && value > 0)
        )]
        const data = {
            message: text,
            category: category,
            queue_ids: normalizedQueueIds,
            queues: normalizedQueueIds,
            is_html: isHtml,
            title: htmlTitle
        }

        if (normalizedQueueIds.length === 0) {
            data.queue_id = 0
        } else if (normalizedQueueIds.length === 1) {
            data.queue_id = normalizedQueueIds[0]
        }
        const url = `/api/enterprises/${enterpriseId}/predefinedmessages/`
        let res = { status: 400 }
        setLoading(true)
        if (id != 0)
            res = await api.patch(`${url}${id}/`, data)
        else
            res = await api.post(url, data)
        setLoading(false)

        if (res.status == 201 || res.status == 200) {
            useNotification(
                'Sucesso!',
                `Mensagem predefinida ${id != 0 ? 'editada' : 'adicionada'} com sucesso.`,
                'success'
            );
            getMessages()
            return true
        }
        else {
            useNotification(
                'Erro!',
                `Ocorreu um erro interno ao ${id != 0 ? 'editar' : 'adicionar'} a mensagem predefinida.`,
                'danger'
            );
            return false
        }
    }

    const deleteCategory = async (category) => {
        setLoading(true)
        const url = `/api/enterprises/${enterpriseId}/predefinedmessages/category/${encodeURIComponent(category)}/`
        const res = await api.delete(url)
        setLoading(false)

        if (res.status == 204) {
            useNotification(
                'Sucesso!',
                `Categoria "${category}" excluída com sucesso.`,
                'success'
            );
            const updatedAllMessages = allMessages.filter(m => m.category !== category)
            setAllMessages(updatedAllMessages)
            filterMessages(updatedAllMessages, selectedSources)
            setShowPredefinedMessages(false)
        }
        else {
            useNotification(
                'Erro!',
                'Ocorreu um erro interno ao excluir a categoria.',
                'danger'
            );
        }
    }

    const getEnterpriseMessages = async () => {
        const enterpriseRes = await api.get(`/api/enterprises/${enterpriseId}/predefinedmessages/`)
        
        const enterpriseMessages = enterpriseRes.status == 200 
            ? enterpriseRes.data.map(msg => ({ ...msg, source: 'enterprise', user_id: null, uniqueId: `enterprise-${msg.id}` }))
            : []
        
        return enterpriseMessages
    }

    const getOperatorsList = async () => {
        const usersRes = await api.get(`/api/enterprises/${enterpriseId}/users/`)
        
        const operatorsList = []
        
        if (usersRes.status == 200) {
            const names = {}
            for (const user of usersRes.data) {
                names[user.id] = user.first_name && user.last_name 
                    ? `${user.first_name} ${user.last_name}`
                    : user.first_name || user.last_name || user.username
            }
            setUserNames(names)

            for (const user of usersRes.data) {
                const userRes = await api.get(`/api/users/${user.id}/predefinedmessages/`)
                if (userRes.status == 200 && Object.keys(userRes.data).length > 0) {
                    const userName = user.first_name && user.last_name 
                        ? `${user.first_name} ${user.last_name}`
                        : user.first_name || user.last_name || user.username
                    operatorsList.push({ id: user.id, name: userName })
                }
            }
        }
        
        return operatorsList
    }

    const loadOperatorMessages = async (userId, force = false, operatorsList = operators) => {
        if (!force && loadedOperators.has(userId)) return []
        
        const userRes = await api.get(`/api/users/${userId}/predefinedmessages/`)
        const userMessages = []
        
        if (userRes.status == 200) {
            const user = operatorsList.find(op => op.id === userId)
            if (user) {
                Object.keys(userRes.data).forEach(category => {
                    userRes.data[category].forEach(msg => {
                        userMessages.push({ 
                            ...msg, 
                            category, 
                            source: 'user', 
                            user_id: user.id,
                            user_name: user.name,
                            uniqueId: `user-${user.id}-${msg.id}`
                        })
                    })
                })
            }
        }
        
        setLoadedOperators(prev => new Set([...prev, userId]))
        return userMessages
    }

    const getMessages = async () => {
        const enterpriseMessages = await getEnterpriseMessages()
        setAllMessages(enterpriseMessages)
        filterMessages(enterpriseMessages, selectedSources)
    }

    const loadOperatorsListOnly = async () => {
        const operatorsList = await getOperatorsList()
        setOperators(operatorsList)
    }

    const filterMessages = (msgs, sources) => {
        const filtered = msgs.filter(m => {
            if (sources.includes('enterprise') && m.source === 'enterprise') return true
            if (sources.includes(m.user_id)) return true
            return false
        })
        setMessages(filtered)
    }

    const filteredMessages = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase()

        if (normalizedSearch.length < 3) {
            return messages
        }

        return messages.filter((message) =>
            (message.message || "").toLowerCase().includes(normalizedSearch)
        )
    }, [messages, search])

    const handleSourceChange = async (event) => {
        const value = event.target.value
        
        if (value.includes("__select_all__")) {
            setLoading(true)
            const allSources = ['enterprise', ...operators.map(op => op.id)]
            
            const newMessages = [...allMessages]
            for (const op of operators) {
                if (!loadedOperators.has(op.id)) {
                    const opMessages = await loadOperatorMessages(op.id)
                    newMessages.push(...opMessages)
                }
            }
            
            setAllMessages(newMessages)
            setSelectedSources(allSources)
            filterMessages(newMessages, allSources)
            setLoading(false)
        } else if (value.includes("__select_none__")) {
            setSelectedSources([])
            filterMessages(allMessages, [])
        } else {
            setLoading(true)
            let newMessages = [...allMessages]
            
            for (const source of value) {
                if (source !== 'enterprise' && !loadedOperators.has(source)) {
                    const opMessages = await loadOperatorMessages(source)
                    newMessages.push(...opMessages)
                }
            }
            
            setAllMessages(newMessages)
            setSelectedSources(value)
            filterMessages(newMessages, value)
            setLoading(false)
        }
    }

    useEffect(async () => {
        setLoading(true)
        await getMessages()
        await loadOperatorsListOnly()
        setLoading(false)
    }, [])

    const getUserName = (userId) => {
        if (!userId) return ''
        return userNames[userId] || `#${userId}`
    }

    const dataColumns = useMemo(() => {
        return [
            { field: 'message', headerName: 'Mensagem', flex: 0.55, filterOperators: stringOperators, 
                renderCell: (params) => {
                    if (params.row.is_html) {
                        return (
                            <Box display="flex" alignItems="center" gap={1}>
                                <CodeIcon color="primary" fontSize="small" />
                                <span>{params.row.title || 'Mensagem HTML'}</span>
                                <Tooltip title="Visualizar HTML">
                                    <IconButton 
                                        size="small" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setHtmlPreview(params.row);
                                        }}
                                    >
                                        <VisibilityIcon fontSize="small" color="primary" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )
                    }
                    return (<span>{emojiReplaceJSX(params.value)}</span>)
                } 
            },
            { field: 'category', headerName: 'Categoria', flex: 0.2, filterOperators: stringOperators },
            {
                field: 'queue',
                headerName: 'Fila',
                flex: 0.2,
                valueGetter: (params) => {
                    const allQueues = getSession().profile?.enterprise?.queues || []
                    const queueIds = Array.isArray(params.row.queue_ids)
                        ? params.row.queue_ids
                        : Array.isArray(params.row.queues)
                            ? params.row.queues
                        : [params.row.queue_id]

                    const normalizedQueueIds = [...new Set(
                        queueIds
                            .map((value) => Number(typeof value === "object" ? value.id ?? value.queue_id ?? value.queue : value))
                            .filter((value) => !Number.isNaN(value) && value > 0)
                    )]

                    if (normalizedQueueIds.length === 0) return "Qualquer Fila"

                    return normalizedQueueIds
                        .map((queueId) => allQueues.find((queue) => queue.id == queueId)?.description)
                        .filter(Boolean)
                        .join(", ")
                },
                filterOperators: stringOperators
            },
            {
                field: 'source',
                headerName: 'Tipo',
                flex: 0.15,
                valueGetter: (params) => params.row.source === 'enterprise' ? 'Geral' : `Operador: ${params.row.user_name}`,
                filterOperators: stringOperators
            },
            {
                field: 'created_by',
                headerName: 'Criado por',
                flex: 0.15,
                valueGetter: (params) => getUserName(params.row.created_by),
                filterOperators: stringOperators
            },
            {
                field: 'updated_by',
                headerName: 'Atualizado por',
                flex: 0.15,
                valueGetter: (params) => getUserName(params.row.updated_by),
                filterOperators: stringOperators
            },
            {
                field: 'action', headerName: 'Ações',
                filterable: false, sortable: false,
                renderCell: (params) => (
                    <>
                        {params.row.source === 'enterprise' && (
                            <>
                                <Button key={`edit-${params.row.uniqueId}`} className="action-button"
                                    onClick={() => { setShowPredefinedMessages(true); setSelected(params.row) }}>
                                    <EditIcon />
                                </Button>
                                <Button key={`delete-${params.row.uniqueId}`} className="action-button delete"
                                    onClick={() => { setShowDeleteDialog(true); setSelected(params.row) }}>
                                    <DeleteIcon />
                                </Button>
                            </>
                        )}
                        {params.row.source === 'user' && (
                            <Button key={`delete-${params.row.uniqueId}`} className="action-button delete"
                                onClick={() => { setShowDeleteDialog(true); setSelected(params.row) }}>
                                <DeleteIcon />
                            </Button>
                        )}
                    </>
                ),
            },
        ]
    }, [userNames])

const renderDeleteText = () => {
    if (!selected) return ''
    return (
        <>
            <Box sx={{ padding: '1rem 0rem' }}>Deseja deletar a mensagem predefinida?</Box>
            <Box><Box sx={{ fontWeight: 'bold' }}>Categoria:</Box> {selected.category}</Box>
            <Box sx={{overflowWrap:'break-word'}}>
                <Box sx={{ fontWeight: 'bold' }}>Mensagem:</Box> 
                {selected.is_html ? (selected.title || 'Mensagem HTML') : emojiReplaceJSX(selected.message)}
            </Box>
        </>
    )
}

return (
    <>
        <Loading loading={loading} />
        {showImportMessages && <ImportMessages
            enterpriseId={enterpriseId}
            handleClose={() => setShowImportMessages(false)}
            onImported={async () => {
                const enterpriseMessages = await getEnterpriseMessages()
                const operatorsList = await getOperatorsList()
                setOperators(operatorsList)
                const updatedMessages = [...enterpriseMessages]
                for (const operator of operatorsList.filter(operator => selectedSources.includes(operator.id))) {
                    updatedMessages.push(...await loadOperatorMessages(operator.id, true, operatorsList))
                }
                setLoadedOperators(new Set(operatorsList.filter(operator => selectedSources.includes(operator.id)).map(operator => operator.id)))
                setAllMessages(updatedMessages)
                filterMessages(updatedMessages, selectedSources)
            }}
        />}
        <Dialog 
            open={!!htmlPreview} 
            onClose={() => setHtmlPreview(null)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{htmlPreview?.title || 'Visualização HTML'}</span>
                <IconButton onClick={() => setHtmlPreview(null)} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ minHeight: 200, p: 2, bgcolor: '#fafafa', borderRadius: 1 }}>
                    {htmlPreview && ReactHtmlParser(htmlPreview.message)}
                </Box>
            </DialogContent>
        </Dialog>
        <ConfirmationDialog open={showDeleteDialog}
            dividers
            title="Excluir mensagem predefinida"
            description={renderDeleteText()}
            handleClose={() => setShowDeleteDialog(false)}
            handleSubmit={deleteMessage}
        />
        {showPredefinedMessages &&
            <PredefinedMessagesModal
                title={`${selected ? 'Editar' : 'Nova'} mensagem predefinida`}
                messages={messages}
                addMessage={addMessage}
                selected={selected}
                handleClose={() => { setShowPredefinedMessages(false); setSelected() }}
                deleteCategory={deleteCategory}
            />
        }
        <Box className="settings-body" >
            <Box className="predefined-settings">
                <Box sx={{ mb: 2 }}>
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                        <FormControl size="small" sx={{ minWidth: 250 }}>
                            <InputLabel id="operators-label">Operadores</InputLabel>
                            <Select
                                multiple
                                labelId="operators-label"
                                label="Operadores"
                                value={selectedSources}
                                onChange={handleSourceChange}
                            >
                                <MenuItem value="__select_all__">Marcar todos</MenuItem>
                                <MenuItem value="__select_none__">Desmarcar todos</MenuItem>
                                <Divider />
                                <MenuItem value="enterprise">Empresa</MenuItem>
                                {operators.map(op => (
                                    <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Buscar mensagem"
                            placeholder="Digite para filtrar"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            sx={{ minWidth: 320 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                </Box>

                <Grid container sx={{ height: '100%' }}>
                    <Box sx={{ width: '100%', height: '100%' }}>
                        <ThemeProvider theme={theme}>
                            <DataGridPro
                                apiRef={apiRef}
                                disableSelectionOnClick
                                disableDensitySelector
                                density="compact"
                                columns={dataColumns}
                                rows={filteredMessages}
                                getRowId={(row) => row.uniqueId}
                                components={{
                                    Toolbar: CustomToolbar,
                                }}
                            />
                        </ThemeProvider>
                    </Box>

                </Grid>
            </Box>
        </Box>
    </>
)
}

export default PredefinedMessages
